import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { BsChevronDown, BsDownload, BsArchive, BsCopy } from 'react-icons/bs';
import { Row } from '../../shared/utils';
import {
  getkycAll,
  deletekyc,
  frontEndBaseURL,
  getkycPDF,
  updateKycArchivedStatus,
  generateShareLink,
} from '../../../API/Api';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import { jwtDecode } from 'jwt-decode';
import Tooltip from '@mui/material/Tooltip';
import 'react-toastify/dist/ReactToastify.css';
import { TokenContext } from '../../../App';
import Loading from '../../shared/Loading';
import FormStatusEnum from '../../../data/FormStatusEnum';

const _KycAmlArchivedCustomerTable = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortDirection, setSortDirection] = useState('desc');
  const [collapsed, setCollapsed] = useState(false); // State for collapsing the table
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const currentLocation = useLocation();
  const [encodedAccessToken] = useContext(TokenContext);
  const decodedToken = jwtDecode(encodedAccessToken);
  const userRole = decodedToken.role;
    const companyId = decodedToken.companyId;

  // 使用 ref 来避免无限循环
  const fetchingRef = useRef(false);

  const fetchData = useCallback(async () => {
    // 如果正在请求，直接返回
    if (fetchingRef.current) {
      return;
    }

    fetchingRef.current = true;

    try {
      setLoading(true);
      const response = await getkycAll(companyId, null, page, pageSize, true); // 添加 archived: true 参数
      // 兼容拦截器返回 undefined 的情况，或后端直接返回数组的情况
      const payload = response?.data ?? response;
      if (!payload) {
        console.error('Empty response from getkycAll');
        throw new Error('EMPTY_RESPONSE');
      }
      const allKycData = Array.isArray(payload) ? { data: payload } : payload;
        const archivedKycData = [];

      // 后端已经筛选出 archived: true 的数据，直接处理即可
      allKycData.data.forEach(item => {
            archivedKycData.push({
              id: item._id,
              name: item.name,
              email: item.email,
          type: item.type,
          submitedDate: item.submitedDate,
          nextAssessmentDate: item.nextRescreeningDate,
          riskLevel: item.riskLevel,
              status: item.formStatus,
          reviewStatus: item.reviewStatus,
          approvalPerson: item.approvalPerson,
              maxPage: item.maxPage,
          companyId: item.companyId,
        });
      });

        setUsers(archivedKycData);

      // Update pagination info
      if (allKycData.pagination) {
        const backendTotal = allKycData.pagination.total;
        const backendPageSize = allKycData.pagination.pageSize || pageSize;
        const backendTotalPages = allKycData.pagination.totalPages || Math.ceil(backendTotal / backendPageSize);

        setPagination(prev => ({
          ...prev,
          page: page,
          pageSize: backendPageSize,
          total: backendTotal,
          totalPages: backendTotalPages,
          hasNextPage: allKycData.pagination.hasNextPage ?? page < backendTotalPages,
          hasPrevPage: allKycData.pagination.hasPrevPage ?? page > 1,
        }));
      } else {
        // If no pagination data from backend, use current page data count
        setPagination(prev => ({
          ...prev,
          page: page,
          pageSize: pageSize,
          total: archivedKycData.length,
          totalPages: 1,
          hasNextPage: false,
          hasPrevPage: page > 1,
        }));
      }

      await new Promise(r => setTimeout(r, 150));
    } catch (error) {
      console.error('Error fetching KYC data:', {
        message: error?.message,
        status: error?.response?.status,
        url: error?.config?.url,
        backend: error?.response?.data,
      });
      const backendMsg = error?.response?.data?.message || error?.message || 'Failed to fetch archived customer data';
      toast.error(backendMsg);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [companyId, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const copyFormLink = async (clientFormId, maxPage, companyId, user) => {
    if (userRole == 'viewer') {
      toast.info('Viewer cannot perform that action');
      return;
    }

    const formTokenData = {
      name: user.name,
      userId: clientFormId,
      companyId: companyId,
    };

    try {
      const shortLinkToCopy = await generateShareLink({
        formTokenData,
        frontEndBaseURL,
        clientFormId,
      });

      if (shortLinkToCopy && shortLinkToCopy.data) {
        await navigator.clipboard.writeText(shortLinkToCopy.data);
        toast.success('External Form Link Copied');
      } else {
        console.error('生成短链失败: 返回数据为空', shortLinkToCopy);
        toast.error('生成分享链接失败，请重试');
      }
    } catch (error) {
      console.error('生成短链时发生错误:', error);
      toast.error('生成分享链接失败，请重试');
    }
  };

  const handleKycPDF = async (userId, userName) => {
    const response = await getkycPDF(userId); // Update the URL with your backend API endpoint
    // const url = window.URL.createObjectURL(blob);
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    const filename = `${userName}_Details_${DateTime.now().setZone('Asia/Singapore').toFormat('dd-MM-yyyy, HH:mm')}.pdf`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
  };

  const handleSearchChange = e => {
    setSearchTerm(e.target.value.toLowerCase());
  };

  const handleSort = column => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('desc');
    }
  };

  const handleDelete = async userId => {
    if (userRole == 'viewer') {
      toast.info('Viewer cannot perform that action');
      return;
    }
    try {
    await deletekyc(userId);
      fetchData(); // Trigger re-fetch from DB
    } catch (error) {
      console.error('Error deleting KYC:', error);
      toast.error('Failed to delete customer');
    }
  };

  const handlePageChange = newPage => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPage(newPage);
    }
  };

  const handlePageSizeChange = e => {
    const newPageSize = parseInt(e.target.value, 10);
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when changing page size
  };

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    const pageButtons = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, pagination.page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous page button
    pageButtons.push(
      <button
        key='prev'
        onClick={() => handlePageChange(pagination.page - 1)}
        disabled={!pagination.hasPrevPage}
        className='px-3 py-1 border rounded mx-1 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100'
      >
        Previous
      </button>
    );

    // First page button (if not visible)
    if (startPage > 1) {
      pageButtons.push(
        <button key={1} onClick={() => handlePageChange(1)} className='px-3 py-1 border rounded mx-1 hover:bg-gray-100'>
          1
        </button>
      );
      if (startPage > 2) {
        pageButtons.push(
          <span key='ellipsis1' className='px-2'>
            ...
          </span>
        );
      }
    }

    // Page number buttons
    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 border rounded mx-1 ${
            pagination.page === i ? 'bg-green-500 text-white' : 'hover:bg-gray-100'
          }`}
        >
          {i}
        </button>
      );
    }

    // Last page button (if not visible)
    if (endPage < pagination.totalPages) {
      if (endPage < pagination.totalPages - 1) {
        pageButtons.push(
          <span key='ellipsis2' className='px-2'>
            ...
          </span>
        );
      }
      pageButtons.push(
        <button
          key={pagination.totalPages}
          onClick={() => handlePageChange(pagination.totalPages)}
          className='px-3 py-1 border rounded mx-1 hover:bg-gray-100'
        >
          {pagination.totalPages}
        </button>
      );
    }

    // Next page button
    pageButtons.push(
      <button
        key='next'
        onClick={() => handlePageChange(pagination.page + 1)}
        disabled={!pagination.hasNextPage}
        className='px-3 py-1 border rounded mx-1 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100'
      >
        Next
      </button>
    );

    return pageButtons;
  };

  const formatSubmittedDate = dateString => {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      const time = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });

      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const dateStr = `${day}-${month}-${year}`;

      return (
        <div className='flex flex-col items-center'>
          <span className='text-base'>{dateStr}</span>
          <span className='text-xs text-gray-500'>{time}</span>
        </div>
      );
    } catch (error) {
      return dateString;
    }
  };

  const getTypeLabel = typeVal => {
    let typeLabel = '-';
    switch (typeVal) {
      case 'individual':
        typeLabel = 'Individual';
        break;
      case 'entity':
        typeLabel = 'Entity';
        break;
      default:
        break;
    }
    return typeLabel;
  };

  const getRiskLevelTag = riskLevel => {
    if (!riskLevel) {
      return '-';
    } else if (riskLevel == 'Low') {
      return <label className='bg-green-200 text-black rounded-full px-2 py-1 text-xs font-semibold'>Low</label>;
    } else if (riskLevel == 'High') {
      return <label className='bg-red-400 text-black rounded-full px-2 py-1 text-xs font-semibold'>High</label>;
    } else if (riskLevel == 'Pending') {
      return <label className='bg-yellow-400 text-black rounded-full px-2 py-1 text-xs font-semibold'>Pending</label>;
    } else if (riskLevel == 'Medium') {
      return <label className='bg-orange-400 text-white rounded-full px-2 py-1 text-xs font-semibold'>Medium</label>;
    } else {
      return (
        <label className='bg-orange-400 text-white rounded-full px-2 py-1 text-xs font-semibold'>{riskLevel}</label>
      );
    }
  };

  const sortedUsers = users.sort((a, b) => {
    if (sortBy === 'lastActivity') {
      if (sortDirection === 'asc') {
        return new Date(a.submitedDate) - new Date(b.submitedDate);
      } else {
        return new Date(b.submitedDate) - new Date(a.submitedDate);
      }
    }
    return 0;
  });

  const filteredUsers = sortedUsers.filter(user => user.name.toLowerCase().includes(searchTerm));

  const handleCollapseToggle = () => {
    setCollapsed(!collapsed);
  };
  const handleUnarchive = async id => {
    // 添加确认对话框
    const isConfirmed = window.confirm('Are you sure you want to unarchive this document?');
    if (!isConfirmed) return;

    try {
      await updateKycArchivedStatus({
        id: id,
        archived: false,
      });

      toast.success('Document unarchived successfully');
      fetchData(); // Trigger re-fetch from DB
    } catch (error) {
      console.error('Error unarchiving:', error);
      toast.error('Failed to unarchive document');
    }
  };
  return (
    <div>
      {/* <ToastContainer pauseOnHover={false} pauseOnFocusLoss={false} /> */}
      <Row>
        <div className='w-full flex justify-between items-center p-4 bg-white shadow'>
          <strong>KYC Archive</strong>
          <input
            type='text'
            placeholder='Search Name'
            value={searchTerm}
            onChange={handleSearchChange}
            className='p-2 border border-gray-300 rounded-lg mr-2 w-1/3'
          />
          <Link to='/kyc-aml/customer-list' replace={currentLocation.pathname === '/'}>
            <button className='bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg'>
              View Active
            </button>
          </Link>
        </div>
      </Row>
      {!collapsed && (
        <div className='relative'>
          {loading ? (
            <Loading message='Loading archived customers...' />
          ) : (
            <div>
              <table className='w-full border-collapse border border-gray-300 shadow table-auto'>
                <thead>
                  <tr className='bg-gray-300 text-left'>
                    <th className='p-3 w-2/12 cursor-pointer' onClick={() => handleSort('name')}>
                      Name{' '}
                      <span className='pl-2 pt-1'>
                        <BsChevronDown className='inline-block' />
                      </span>
                    </th>
                    <th className='p-2 w-2/12 text-center'>
                      <div className='flex flex-col items-center'>
                        <span>Submitted</span>
                        <span>Date</span>
                      </div>
                    </th>
                    <th className='p-2 w-1/12 text-center'>Type</th>
                    <th className='p-2 w-1/12 text-center'>Risk level</th>
                    <th className='p-2 w-2/12 text-center'>Status</th>
                    <th className='p-2 w-1/12 text-center'>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan='6' className='p-4 text-center text-gray-500'>
                        No archived customers found
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((user, index) => (
                    <tr
                      key={user.id}
                      className={index % 2 === 0 ? 'bg-green-50 hover:bg-green-100' : 'bg-white hover:bg-green-100'}
                    >
                        <td className='p-3 w-2/12'>
                      <Link
                        to={`/kyc-aml/form?clientFormId=${user.id}&page=${user.maxPage}`}
                        replace={
                          currentLocation.pathname ===
                          `/kyc-aml/overview-assessment?clientFormId=${user.id}&page=${user.maxPage}`
                        }
                      >
                        <Tooltip
                          title={
                            <p style={{ fontSize: '14px' }}>
                                  Edit {user.type}, {user.name}
                            </p>
                          }
                          className='border-stone-500'
                          arrow
                        >
                              <span className='cursor-pointer hover:underline'>{user.name}</span>
                        </Tooltip>
                      </Link>
                        </td>
                        <td className='p-2 w-2/12 text-center'>{formatSubmittedDate(user.submitedDate)}</td>
                        <td className='p-2 w-1/12 text-center'>{getTypeLabel(user.type)}</td>
                        <td className='p-2 w-1/12 text-xs text-center'>{getRiskLevelTag(user.riskLevel)}</td>
                        <td className='p-2 w-2/12 text-xs text-center'>
                          <label className='bg-purple-500 text-white rounded-full px-2 py-1 text-xs font-semibold'>
                            Archived
                          </label>
                      </td>
                        <td className='p-2 w-1/12 text-center'>
                          <div className='flex justify-center'>
                          <Tooltip
                            title={'Copy Form Link'}
                              className='p-2.5 mx-1 rounded-full border border-stone-500 cursor-pointer hover:bg-gray-400 transition duration-500'
                            arrow
                          >
                              <div onClick={() => copyFormLink(user.id, user.maxPage, user.companyId, user)}>
                                <BsCopy />
                              </div>
                          </Tooltip>
                          <Tooltip
                            title={'Download'}
                            className='p-2.5 mx-1 rounded-full cursor-pointer hover:bg-gray-400 border border-stone-500 transition duration-500'
                            arrow
                          >
                              <div onClick={() => handleKycPDF(user.id, user.name)}>
                                <BsDownload />
                              </div>
                          </Tooltip>
                          <Tooltip
                            title={'Delete'}
                            className='p-2.5 mx-1 rounded-full cursor-pointer hover:bg-gray-400 border border-stone-500 transition duration-500'
                            arrow
                          >
                              <div onClick={() => handleDelete(user.id)}>
                                <BsArchive />
                              </div>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                    ))
                  )}
                </tbody>
              </table>
              {/* Pagination Controls */}
              {pagination.totalPages > 1 && (
                <div className='flex justify-between items-center mt-4 p-4 bg-white'>
                  <div className='flex items-center gap-4'>
                    <div className='text-sm text-gray-600'>
                      Showing {(pagination.page - 1) * pagination.pageSize + 1} -{' '}
                      {Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total} records
                    </div>
                    <div className='flex items-center gap-2'>
                      <label htmlFor='pageSize' className='text-sm text-gray-600'>
                        Show:
                      </label>
                      <select
                        id='pageSize'
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        className='border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                        disabled={loading}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span className='text-sm text-gray-600'>per page</span>
                    </div>
                  </div>
                  <div className='flex items-center'>{renderPagination()}</div>
                </div>
              )}
              {/* Show page size selector even when there's only one page */}
              {pagination.totalPages <= 1 && pagination.total > 0 && (
                <div className='flex justify-end items-center mt-4 p-4 bg-white'>
                  <div className='flex items-center gap-2'>
                    <label htmlFor='pageSizeSingle' className='text-sm text-gray-600'>
                      Show:
                    </label>
                    <select
                      id='pageSizeSingle'
                      value={pageSize}
                      onChange={handlePageSizeChange}
                      className='border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                      disabled={loading}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className='text-sm text-gray-600'>per page</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default _KycAmlArchivedCustomerTable;
